/**
 * Rendus PARTAGÉS des blocs Puck (source unique de la présentation).
 *
 * Chaque fonction est PURE : (props, theme) → JSX. Aucune dépendance à un
 * routeur, à Puck, à Cloudinary ou à une lib externe — uniquement React, des
 * inline styles et le thème PCC. C'est ce qui rend ces rendus partageables
 * tels quels entre l'app (pccapp) et l'éditeur intranet (pcc-bureau).
 *
 * `theme` ('light' | 'dark') fait basculer les surfaces neutres (fond de
 * carte, texte, encadrés) — les accents ne bougent pas. L'app passe le thème
 * du téléphone ; l'éditeur Puck appelle `render(props)` (sans 2e argument) donc
 * l'aperçu reste TOUJOURS en clair → aperçu éditeur = rendu app (en clair).
 *
 * ⚠️ Ne pas éditer les copies vendues : modifier le dépôt canonique
 * pcc-puck-blocks puis `npm run sync-puck-blocks` dans chaque dépôt.
 */
import React from 'react';
import { PCC, frenchDate, surface } from './theme';

// Attributs de lien d'un bloc : un lien INTERNE (« /page/… », « /events »…)
// navigue dans l'app — l'app intercepte le clic pour une navigation SPA fluide,
// et sans intercepteur un `<a href>` simple navigue quand même. Un lien EXTERNE
// (http(s), mailto…) s'ouvre dans un nouvel onglet.
function linkAttrs(href) {
  const internal = typeof href === 'string' && href.startsWith('/') && !href.startsWith('//');
  return internal
    ? { href }
    : { href: href || '#', target: '_blank', rel: 'noopener noreferrer' };
}

// Styles d'apparence choisis par l'éditrice (couleur titre/texte, police,
// taille/graisse du titre). Appliqués en inline → identiques app + aperçu.
const FONT_STACK = {
  display: "'Playfair Display', Georgia, serif",
  serif: "Georgia, 'Times New Roman', serif",
  sans: "'Helvetica Neue', Arial, sans-serif",
};
const TITLE_SIZE = { sm: '15px', md: '18px', lg: '22px', xl: '28px' };
const TITLE_WEIGHT = { normal: 500, bold: 700 };
export function styleFrom(style, theme = 'light') {
  const st = style || {};
  const font = FONT_STACK[st.fontFamily];
  const title = {};
  const text = {};
  // En mode sombre, un texte choisi « foncé » (#272727) deviendrait illisible :
  // on le bascule en crème (étape 5, Apparence).
  const fixDark = (c) => (theme === 'dark' && typeof c === 'string' && c.toLowerCase() === '#272727' ? '#F2F1EB' : c);
  if (st.titleColor) title.color = fixDark(st.titleColor);
  if (font) { title.fontFamily = font; text.fontFamily = font; }
  if (TITLE_SIZE[st.titleSize]) title.fontSize = TITLE_SIZE[st.titleSize];
  if (TITLE_WEIGHT[st.titleWeight]) title.fontWeight = TITLE_WEIGHT[st.titleWeight];
  if (st.textColor) text.color = fixDark(st.textColor);
  return { title, text, font };
}

// ─── Bannière ────────────────────────────────────────────────────────────
const BANNER_ICONS = {
  cross: 'M12 3v18M3 12h18',
  bell: 'M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0',
  heart: 'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z',
  calendar: 'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0V11.25a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 11.25v7.5',
  info: 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z',
  megaphone: 'M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535',
};

// La bannière utilise les couleurs CHOISIES par l'éditrice (accent) → pas de
// bascule de thème.
export function renderBanner({ message, icon, backgroundColor, textColor, ctaText, ctaUrl }) {
  const path = BANNER_ICONS[icon] || BANNER_ICONS.info;
  return (
    <div
      className="max-w-2xl mx-auto flex items-center gap-3 rounded-xl px-4 py-3.5"
      style={{ backgroundColor: backgroundColor || PCC.gold, color: textColor || '#FFFFFF' }}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="flex-shrink-0">
        <path strokeLinecap="round" strokeLinejoin="round" d={path} />
      </svg>
      <p className="flex-1 text-[15px] font-medium leading-snug">{message}</p>
      {ctaText && (
        <a
          href={ctaUrl || '#'}
          className="flex-shrink-0 px-3.5 py-1.5 rounded-lg text-[13px] font-semibold transition-opacity hover:opacity-90"
          style={{ backgroundColor: textColor || '#FFFFFF', color: backgroundColor || PCC.gold }}
        >
          {ctaText}
        </a>
      )}
    </div>
  );
}

// ─── Hero (accueil) ──────────────────────────────────────────────────────
// Le Hero est déjà une section sombre (image + voile) → lisible en clair comme
// en sombre, pas de bascule.
export function renderHero({ title, subtitle, backgroundImage, ctaText, ctaUrl, overlayOpacity, textColor, alignment, style, height = 'standard' }, theme = 'light') {
  const st = styleFrom(style, theme);
  const minH = { standard: 320, large: 420, xl: 540 }[height] || 320;
  const color = textColor === 'black' ? PCC.charcoal : '#FFFFFF';
  const align = alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center';
  const textAlign = alignment || 'center';
  const opacity = typeof overlayOpacity === 'number' ? overlayOpacity : 0.4;
  return (
    <section
      className="relative w-full flex items-center overflow-hidden"
      style={{
        minHeight: minH,
        backgroundColor: backgroundImage ? '#000' : PCC.charcoal,
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Voile sombre pour la lisibilité */}
      <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${opacity})` }} />
      <div
        className="relative w-full px-6 sm:px-10 py-16 flex flex-col gap-4"
        style={{ alignItems: align, textAlign, fontFamily: st.font }}
      >
        <h1 className="text-3xl sm:text-5xl font-bold leading-tight max-w-3xl" style={{ color, ...st.title }}>{title}</h1>
        {subtitle && <p className="text-lg sm:text-xl max-w-2xl" style={{ color, opacity: 0.92, ...st.text }}>{subtitle}</p>}
        {ctaText && (
          <a
            href={ctaUrl || '#'}
            className="mt-2 inline-block px-6 py-3 rounded-xl text-white text-[16px] font-semibold transition-opacity hover:opacity-90"
            style={{ backgroundColor: PCC.gold }}
          >
            {ctaText}
          </a>
        )}
      </div>
    </section>
  );
}

// ─── Événement ───────────────────────────────────────────────────────────
export function renderEvent({ title, subtitle, startDate, endDate, location, image, description, ctaText, ctaUrl, registrationUrl, registrationLabel, featured }, theme = 'light') {
  const s = surface(theme);
  const start = frenchDate(startDate);
  const end = frenchDate(endDate);
  const dateLabel = start && end && end !== start ? `Du ${start} au ${end}` : start;
  return (
    <div
      className="max-w-2xl mx-auto overflow-hidden rounded-2xl shadow-sm border"
      style={{ backgroundColor: s.card, borderColor: `${PCC.gold}55` }}
    >
      {image ? (
        <img src={image} alt={title} className="w-full h-52 object-cover" />
      ) : (
        <div className="w-full h-40 flex items-center justify-center" style={{ backgroundColor: `${PCC.gold}22`, color: PCC.gold }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5A2.25 2.25 0 015.25 5.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0V11.25A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
          </svg>
        </div>
      )}
      <div className="p-6">
        {featured && (
          <span className="inline-block text-[11px] font-semibold uppercase tracking-wider mb-2 px-2 py-0.5 rounded-md text-white" style={{ backgroundColor: PCC.gold }}>
            À la une
          </span>
        )}
        {dateLabel && (
          <p className="text-[13px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: PCC.gold }}>
            {dateLabel}
          </p>
        )}
        <h3 className="text-2xl font-bold leading-tight" style={{ color: s.text }}>{title}</h3>
        {subtitle && <p className="text-[15px] mt-1" style={{ color: `${s.text}B0` }}>{subtitle}</p>}
        {location && (
          <p className="text-[15px] mt-3 flex items-center gap-1.5" style={{ color: `${s.text}99` }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
            </svg>
            {location}
          </p>
        )}
        {description && <p className="text-[15px] mt-3 leading-relaxed whitespace-pre-wrap" style={{ color: `${s.text}CC` }}>{description}</p>}
        <div className="flex flex-wrap gap-2.5 mt-5">
          {ctaText && (
            <a href={ctaUrl || '#'} className="inline-block px-5 py-2.5 rounded-xl text-white text-[15px] font-medium transition-opacity hover:opacity-90" style={{ backgroundColor: PCC.blue }}>
              {ctaText}
            </a>
          )}
          {registrationUrl && (
            <a href={registrationUrl} className="inline-block px-5 py-2.5 rounded-xl text-[15px] font-medium border transition-colors hover:opacity-90" style={{ borderColor: PCC.gold, color: PCC.gold, backgroundColor: 'transparent' }}>
              {registrationLabel || "S'inscrire"}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Événement PCC (enrichi) ─────────────────────────────────────────────
const PCC_EVENT_TYPE_META = {
  culte: { label: 'Culte', color: PCC.gold },
  camp: { label: 'Camp', color: PCC.blue },
  formation: { label: 'Formation', color: PCC.green },
  evangelisation: { label: 'Évangélisation', color: '#D7823C' },
  autre: { label: 'Autre', color: '#8A8578' },
};

export function renderPCCEvent({ title, subtitle, startDate, endDate, location, image, description, type, showRegistration, registrationUrl, registrationLabel, maxParticipants, pastoralNotes, tags, featured, puck }, theme = 'light') {
  const s = surface(theme);
  const meta = PCC_EVENT_TYPE_META[type] || PCC_EVENT_TYPE_META.autre;
  const start = frenchDate(startDate);
  const end = frenchDate(endDate);
  const dateLabel = start && end && end !== start ? `Du ${start} au ${end}` : start;
  const tagList = Array.isArray(tags) ? tags : [];
  return (
    <div
      className="max-w-2xl mx-auto overflow-hidden rounded-2xl border"
      style={{
        backgroundColor: s.card,
        borderColor: featured ? PCC.gold : `${PCC.gold}44`,
        boxShadow: featured ? `0 0 0 2px ${PCC.gold}55, 0 8px 24px ${PCC.gold}22` : '0 1px 2px rgba(0,0,0,0.05)',
      }}
    >
      {image ? (
        <img src={image} alt={title} className="w-full h-48 object-cover" />
      ) : (
        <div className="w-full h-32 flex items-center justify-center" style={{ backgroundColor: `${meta.color}18`, color: meta.color }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5A2.25 2.25 0 015.25 5.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0V11.25A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
          </svg>
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md text-white" style={{ backgroundColor: meta.color }}>
            {meta.label}
          </span>
          {featured && <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md text-white" style={{ backgroundColor: PCC.gold }}>À la une</span>}
        </div>
        {dateLabel && <p className="text-[13px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: meta.color }}>{dateLabel}</p>}
        <h3 className="text-2xl font-bold leading-tight" style={{ color: s.text }}>{title}</h3>
        {subtitle && <p className="text-[15px] mt-1" style={{ color: `${s.text}B0` }}>{subtitle}</p>}
        {location && (
          <p className="text-[15px] mt-3 flex items-center gap-1.5" style={{ color: `${s.text}99` }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
            </svg>
            {location}
          </p>
        )}
        {tagList.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {tagList.map((t) => (
              <span key={t} className="text-[12px] font-medium px-2.5 py-0.5 rounded-full" style={{ backgroundColor: `${PCC.blue}18`, color: PCC.blue }}>{t}</span>
            ))}
          </div>
        )}
        {description && <p className="text-[15px] mt-3 leading-relaxed whitespace-pre-wrap" style={{ color: `${s.text}CC` }}>{description}</p>}
        {showRegistration && registrationUrl && (
          <div className="mt-5">
            <a href={registrationUrl} className="inline-block px-5 py-2.5 rounded-xl text-white text-[15px] font-medium transition-opacity hover:opacity-90" style={{ backgroundColor: PCC.blue }}>
              {registrationLabel || "S'inscrire"}
            </a>
            {maxParticipants ? <span className="ml-3 text-[13px]" style={{ color: `${s.text}80` }}>{maxParticipants} places</span> : null}
          </div>
        )}

        {/* Notes pastorales — visibles UNIQUEMENT dans l'éditeur CMS (puck.isEditing),
            jamais dans l'app (puck est undefined ici, et l'API publique les retire).
            L'éditeur étant toujours en clair, on garde l'encadré jaune clair. */}
        {puck?.isEditing && pastoralNotes && (
          <div className="mt-5 rounded-lg p-3 text-[13px]" style={{ backgroundColor: '#FFF7E8', border: `1px dashed ${PCC.gold}` }}>
            <span className="font-semibold" style={{ color: PCC.gold }}>Note interne (non publiée) : </span>
            <span style={{ color: `${PCC.charcoal}CC` }}>{pastoralNotes}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Don / Soutien ───────────────────────────────────────────────────────
// Icônes proposées pour le bloc Don (l'éditrice choisit ; « heart » par défaut).
export const DONATION_ICONS = {
  heart: 'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z',
  gift: 'M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z',
  church: 'M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z',
  sparkles: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z',
};

export function renderDonation({ title, description, iban, bic, accountName, donorboxUrl, ctaText, showIban, showOnline, icon, transferTitle }, theme = 'light') {
  const s = surface(theme);
  const iconPath = DONATION_ICONS[icon] || DONATION_ICONS.heart;
  const copy = (text) => { try { navigator.clipboard?.writeText(text); } catch { /* noop */ } };
  return (
    <div
      className="max-w-2xl mx-auto rounded-2xl border p-6"
      style={{ backgroundColor: s.card, borderColor: `${PCC.gold}55` }}
    >
      <div className="flex items-center gap-2.5 mb-2">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={PCC.gold} strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
        </svg>
        <h3 className="text-xl font-bold" style={{ color: s.text }}>{title}</h3>
      </div>
      {description && <p className="text-[15px] leading-relaxed mb-4" style={{ color: `${s.text}CC` }}>{description}</p>}

      {showIban && (iban || bic || accountName) && (
        <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: s.inner, border: `1px dashed ${PCC.gold}77` }}>
          <p className="text-[12px] font-semibold uppercase tracking-wider mb-2" style={{ color: PCC.gold }}>{transferTitle || 'Par virement'}</p>
          {accountName && <p className="text-[14px]" style={{ color: s.text }}><span style={{ color: `${s.text}80` }}>Titulaire : </span>{accountName}</p>}
          {iban && (
            <div className="flex items-center justify-between gap-2 mt-1">
              <p className="text-[14px] font-mono" style={{ color: s.text }}><span className="font-sans" style={{ color: `${s.text}80` }}>IBAN : </span>{iban}</p>
              <button type="button" onClick={() => copy(iban)} className="text-[12px] font-medium hover:underline flex-shrink-0" style={{ color: PCC.blue }}>Copier</button>
            </div>
          )}
          {bic && <p className="text-[14px] font-mono mt-1" style={{ color: s.text }}><span className="font-sans" style={{ color: `${s.text}80` }}>BIC : </span>{bic}</p>}
        </div>
      )}

      {showOnline && donorboxUrl && (
        <a
          href={donorboxUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block px-6 py-3 rounded-xl text-white text-[15px] font-semibold transition-opacity hover:opacity-90"
          style={{ backgroundColor: PCC.gold }}
        >
          {ctaText || 'Faire un don en ligne'}
        </a>
      )}
    </div>
  );
}

// ─── Plan de lecture ─────────────────────────────────────────────────────
const READING_PLAN_CAT_META = {
  priere: { label: 'Prière', color: PCC.violet },
  evangelisation: { label: 'Évangélisation', color: '#D7823C' },
  croissance: { label: 'Croissance', color: PCC.green },
  vie_chretienne: { label: 'Vie chrétienne', color: PCC.blue },
  famille: { label: 'Famille', color: PCC.gold },
  autre: { label: 'Autre', color: '#8A8578' },
};

export function renderReadingPlan({ title, description, coverImage, youVersionUrl, duration, category, featured, authorSignature, ctaLabel }, theme = 'light') {
  const s = surface(theme);
  const meta = READING_PLAN_CAT_META[category] || READING_PLAN_CAT_META.autre;
  return (
    <div
      className="max-w-2xl mx-auto overflow-hidden rounded-2xl border flex flex-col sm:flex-row"
      style={{
        backgroundColor: s.card,
        borderColor: featured ? PCC.gold : `${PCC.gold}44`,
        boxShadow: featured ? `0 0 0 2px ${PCC.gold}55, 0 8px 24px ${PCC.gold}22` : '0 1px 2px rgba(0,0,0,0.05)',
      }}
    >
      <div className="sm:w-40 flex-shrink-0">
        {coverImage ? (
          <img src={coverImage} alt={title} className="w-full h-40 sm:h-full object-cover" />
        ) : (
          <div className="w-full h-40 sm:h-full flex items-center justify-center" style={{ backgroundColor: `${meta.color}18`, color: meta.color }}>
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
          </div>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md text-white" style={{ backgroundColor: meta.color }}>{meta.label}</span>
          {featured && <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md text-white" style={{ backgroundColor: PCC.gold }}>À la une</span>}
        </div>
        <h3 className="text-xl font-bold leading-tight" style={{ color: s.text }}>{title}</h3>
        {description && <p className="text-[14px] mt-1.5 leading-relaxed" style={{ color: `${s.text}CC` }}>{description}</p>}
        {duration && (
          <span className="inline-flex items-center gap-1.5 mt-3 self-start px-2.5 py-1 rounded-full text-[12px] font-medium" style={{ backgroundColor: `${PCC.gold}18`, color: PCC.gold }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 8.25h18M4.5 5.25h15A1.5 1.5 0 0121 6.75v12.75A1.5 1.5 0 0119.5 21h-15A1.5 1.5 0 013 19.5V6.75A1.5 1.5 0 014.5 5.25z" />
            </svg>
            {duration}
          </span>
        )}
        <div className="mt-4">
          <a
            href={youVersionUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-5 py-2.5 rounded-xl text-white text-[15px] font-medium transition-opacity hover:opacity-90"
            style={{ backgroundColor: PCC.blue }}
          >
            {ctaLabel || 'Commencer sur YouVersion'}
          </a>
        </div>
        {authorSignature && <p className="text-[12px] italic mt-4" style={{ color: `${s.text}80` }}>— {authorSignature}</p>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Blocs GÉNÉRIQUES (set Essentiel) — briques réutilisables permettant de
// composer n'importe quelle page sans nouveau code (levier « CMS sans MAJ »).
// ═══════════════════════════════════════════════════════════════════════════

// ─── Texte riche ───────────────────────────────────────────────────────────
// Titre optionnel + paragraphe. Bascule de thème sur la couleur du texte
// (surface neutre). Les sauts de ligne saisis par l'éditrice sont préservés.
export function renderText({ title, body, align = 'left', size = 'normal', style }, theme = 'light') {
  const s = surface(theme);
  const st = styleFrom(style, theme);
  const titleCls = size === 'large' ? 'text-[24px]' : 'text-[19px]';
  const bodyCls = size === 'large' ? 'text-[17px]' : 'text-[15px]';
  return (
    <div className="max-w-2xl mx-auto px-1" style={{ textAlign: align, color: s.text, fontFamily: st.font }}>
      {title && <h3 className={`${titleCls} font-semibold leading-snug mb-2`} style={st.title}>{title}</h3>}
      {body && <p className={`${bodyCls} leading-relaxed whitespace-pre-line`} style={{ color: `${s.text}CC`, ...st.text }}>{body}</p>}
    </div>
  );
}

// ─── Image ─────────────────────────────────────────────────────────────────
// Image + légende optionnelle + lien optionnel (nouvel onglet). `rounded`
// arrondit les coins. Rendu neutre → pas de bascule de thème sur l'image.
export function renderImage({ src, alt, caption, href, rounded = true, width = 'lg' }, theme = 'light') {
  const s = surface(theme);
  if (!src) return null;
  const widthCls = { sm: 'max-w-xs', md: 'max-w-md', lg: 'max-w-2xl', full: 'max-w-full' }[width] || 'max-w-2xl';
  const img = (
    <img src={src} alt={alt || caption || ''} className={`w-full h-auto object-cover ${rounded ? 'rounded-2xl' : ''}`} />
  );
  return (
    <figure className={`${widthCls} mx-auto`}>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="block">{img}</a>
      ) : img}
      {caption && <figcaption className="text-[13px] mt-2 text-center" style={{ color: `${s.text}99` }}>{caption}</figcaption>}
    </figure>
  );
}

// ─── Bouton / CTA ──────────────────────────────────────────────────────────
// Bouton d'appel à l'action. Style plein (accent) ou contour. Couleur d'accent
// choisie par l'éditrice → pas de bascule de thème.
export function renderButton({ label, url, variant = 'solid', color, align = 'center' }) {
  if (!label) return null;
  const accent = color || PCC.blue;
  const justify = align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center';
  const style = variant === 'outline'
    ? { border: `1.5px solid ${accent}`, color: accent, backgroundColor: 'transparent' }
    : { backgroundColor: accent, color: '#FFFFFF' };
  return (
    <div className={`max-w-2xl mx-auto flex ${justify}`}>
      <a
        {...linkAttrs(url)}
        className="inline-block px-5 py-2.5 rounded-xl text-[15px] font-semibold transition-opacity hover:opacity-90"
        style={style}
      >
        {label}
      </a>
    </div>
  );
}

// ─── Vidéo (YouTube) ─────────────────────────────────────────────────────────
// Intègre une vidéo YouTube (accepte watch?v=, youtu.be, embed, shorts). Ratio
// 16:9. Titre + légende optionnels.
function youTubeId(url) {
  if (!url) return '';
  const m = String(url).match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/);
  return m ? m[1] : '';
}
export function renderVideo({ url, title, caption }, theme = 'light') {
  const s = surface(theme);
  const id = youTubeId(url);
  return (
    <div className="max-w-2xl mx-auto">
      {title && <h3 className="text-[19px] font-semibold leading-snug mb-2" style={{ color: s.text }}>{title}</h3>}
      {id ? (
        <div className="relative w-full overflow-hidden rounded-2xl" style={{ paddingTop: '56.25%', backgroundColor: '#000' }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${id}`}
            title={title || 'Vidéo'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <div className="rounded-2xl px-4 py-6 text-center text-[13px]" style={{ backgroundColor: s.card, color: `${s.text}99` }}>
          Lien YouTube manquant ou invalide.
        </div>
      )}
      {caption && <p className="text-[13px] mt-2 text-center" style={{ color: `${s.text}99` }}>{caption}</p>}
    </div>
  );
}

// ─── Séparateur / Espace ─────────────────────────────────────────────────────
// `variant`: 'space' (espace vide) ou 'divider' (filet horizontal).
// `size`: 'sm' | 'md' | 'lg'.
export function renderSpacer({ variant = 'space', size = 'md' }, theme = 'light') {
  const s = surface(theme);
  const h = size === 'sm' ? 12 : size === 'lg' ? 48 : 24;
  if (variant === 'divider') {
    return (
      <div className="max-w-2xl mx-auto" style={{ paddingTop: h / 2, paddingBottom: h / 2 }}>
        <hr style={{ border: 0, borderTop: `1px solid ${s.text}22` }} />
      </div>
    );
  }
  return <div style={{ height: h }} aria-hidden="true" />;
}

// ─── Carte ───────────────────────────────────────────────────────────────────
// Tuile : image optionnelle + titre + texte + lien optionnel (carte entière
// cliquable). Surface theme-aware.
export function renderCard({ image, title, text, href, ctaText, style, imageHeight = 'md' }, theme = 'light') {
  const s = surface(theme);
  const st = styleFrom(style, theme);
  const imgH = { sm: 'h-32', md: 'h-44', lg: 'h-60' }[imageHeight] || 'h-44';
  const inner = (
    <>
      {image && <img src={image} alt={title || ''} className={`w-full ${imgH} object-cover`} />}
      <div className="p-4" style={{ fontFamily: st.font }}>
        {title && <h3 className="text-[17px] font-semibold leading-snug" style={{ color: s.text, ...st.title }}>{title}</h3>}
        {text && <p className="text-[14px] leading-relaxed mt-1.5 whitespace-pre-line" style={{ color: `${s.text}CC`, ...st.text }}>{text}</p>}
        {ctaText && <span className="inline-block mt-3 text-[14px] font-semibold" style={{ color: PCC.blue }}>{ctaText} →</span>}
      </div>
    </>
  );
  const cls = 'max-w-2xl mx-auto rounded-2xl overflow-hidden';
  const cardStyle = { backgroundColor: s.card, border: `1px solid ${s.text}14` };
  if (href) {
    return (
      <a {...linkAttrs(href)} className={`block ${cls} transition-opacity hover:opacity-95`} style={cardStyle}>
        {inner}
      </a>
    );
  }
  return <div className={cls} style={cardStyle}>{inner}</div>;
}

// ─── Accordéon / FAQ ─────────────────────────────────────────────────────────
// Liste de questions/réponses repliables. `<details>/<summary>` natifs : aucun
// JS, aucun hook → sûr dans un rendu pur (app) comme dans l'aperçu Puck. Le
// « + » pivote à l'ouverture (variante Tailwind group-open ; sans elle, le bloc
// reste fonctionnel).
export function renderFaq({ title, items, style }, theme = 'light') {
  const s = surface(theme);
  const st = styleFrom(style, theme);
  const list = Array.isArray(items) ? items : [];
  return (
    <div className="max-w-2xl mx-auto" style={{ fontFamily: st.font }}>
      {title && <h3 className="text-[19px] font-semibold leading-snug mb-3" style={{ color: s.text, ...st.title }}>{title}</h3>}
      <div className="space-y-2">
        {list.map((it, i) => (
          <details key={i} className="group rounded-xl overflow-hidden" style={{ backgroundColor: s.card, border: `1px solid ${s.text}14` }}>
            <summary
              className="cursor-pointer list-none px-4 py-3 text-[15px] font-semibold flex items-center justify-between gap-3"
              style={{ color: s.text }}
            >
              <span>{it?.question}</span>
              <span className="text-[20px] leading-none transition-transform group-open:rotate-45" style={{ color: `${s.text}80` }}>+</span>
            </summary>
            <div className="px-4 pb-4 text-[14px] leading-relaxed whitespace-pre-line" style={{ color: `${s.text}CC`, ...st.text }}>
              {it?.answer}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

// ─── Carrousel ───────────────────────────────────────────────────────────────
// Défilement horizontal de visuels (image + titre/sous-titre + lien facultatif).
// CSS pur (scroll-snap) : aucun JS, aucun hook → glisse au doigt sur mobile,
// identique dans l'app et dans l'aperçu Puck. Chaque visuel laisse « dépasser »
// le suivant pour indiquer qu'on peut faire défiler.
export function renderCarousel({ slides, rounded = true, style, ratio = '16/10', slideWidth = 'large' }, theme = 'light') {
  const s = surface(theme);
  const st = styleFrom(style, theme);
  const list = Array.isArray(slides) ? slides.filter((sl) => sl && sl.image) : [];
  if (list.length === 0) return null;
  const radius = rounded ? 'rounded-2xl' : '';
  const widthCls = { full: 'w-full max-w-2xl', large: 'w-[85%] sm:w-[62%] max-w-xl', medium: 'w-[70%] sm:w-[50%] max-w-md' }[slideWidth] || 'w-[85%] sm:w-[62%] max-w-xl';
  const ar = { '16/9': '16 / 9', '16/10': '16 / 10', '4/3': '4 / 3', '1/1': '1 / 1' }[ratio] || '16 / 10';
  const slideCls = `relative snap-center shrink-0 ${widthCls} overflow-hidden ${radius}`;
  const slideStyle = { aspectRatio: ar, backgroundColor: s.card };
  return (
    <div
      className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2"
      style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
    >
      {list.map((sl, i) => {
        const body = (
          <>
            <img src={sl.image} alt={sl.title || ''} className="absolute inset-0 w-full h-full object-cover" />
            {(sl.title || sl.subtitle) && (
              <>
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.66), rgba(0,0,0,0.10) 46%, rgba(0,0,0,0) 72%)' }}
                />
                <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                  {sl.title && <h3 className="text-[18px] font-semibold leading-snug" style={st.title}>{sl.title}</h3>}
                  {sl.subtitle && <p className="text-[13px] mt-0.5 leading-snug" style={{ opacity: 0.92, ...st.text }}>{sl.subtitle}</p>}
                </div>
              </>
            )}
          </>
        );
        return sl.href
          ? <a key={i} {...linkAttrs(sl.href)} className={slideCls} style={slideStyle}>{body}</a>
          : <div key={i} className={slideCls} style={slideStyle}>{body}</div>;
      })}
    </div>
  );
}
